package com.eladfinish.bible.contest.screens.monopoly

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.dice3droll
import com.eladfinish.bible.contest.dice_1
import com.eladfinish.bible.contest.dice_2
import com.eladfinish.bible.contest.dice_3
import com.eladfinish.bible.contest.dice_4
import com.eladfinish.bible.contest.dice_5
import com.eladfinish.bible.contest.dice_6
import com.eladfinish.bible.contest.screens.monopoly.usecases.GetMonopolyDataUseCase
import kotlinx.collections.immutable.persistentListOf
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.random.Random
import kotlin.time.Duration.Companion.milliseconds

class MonopolyStore(
    private val getMonopolyDataUseCase: GetMonopolyDataUseCase,
) : ViewModelStore<MonopolyState, MonopolyAction, MonopolyEffect>(
    tag = "MonopolyStore",
    sideEffectFlow = MutableSharedFlow(
        extraBufferCapacity = 4,
        onBufferOverflow = BufferOverflow.DROP_OLDEST,
    ),
) {

    private val diceImages = listOf(
        SharedRes.images.dice_1,
        SharedRes.images.dice_2,
        SharedRes.images.dice_3,
        SharedRes.images.dice_4,
        SharedRes.images.dice_5,
        SharedRes.images.dice_6,
    )

    override val stateFlow = MutableStateFlow(
        MonopolyState(
            pasukText = "",
            diceImage = SharedRes.images.dice3droll,
            isRolling = false,
            isRevealVisible = false,
            revealText = "",
            fines = persistentListOf(),
            prizes = persistentListOf(),
            questions = persistentListOf(),
            isLoading = false,
        )
    )

    init {
        loadData()
    }

    private fun loadData() {
        updateState { it.copy(isLoading = true) }
        launchEffect {
            val statementsResult = getMonopolyDataUseCase.getStatements()
            statementsResult.onSuccess { (fines, prizes) ->
                dispatch(MonopolyAction.StatementsLoaded(fines = fines, prizes = prizes))
            }.onFailure {
                dispatch(MonopolyAction.LoadError(it.message))
            }

            val questionsResult = getMonopolyDataUseCase.getQuestions()
            questionsResult.onSuccess { questions ->
                dispatch(MonopolyAction.QuestionsLoaded(questions = questions))
            }.onFailure {
                dispatch(MonopolyAction.LoadError(it.message))
            }

            updateState { it.copy(isLoading = false) }
        }
    }

    override fun handleAction(action: MonopolyAction): MonopolyState =
        when (action) {
            MonopolyAction.DiceClicked -> handleDiceClicked()
            MonopolyAction.DiceRollFinished -> handleDiceRollFinished()
            MonopolyAction.QuestionClicked -> handleQuestionClicked()
            MonopolyAction.PrizeClicked -> handlePrizeClicked()
            MonopolyAction.FineClicked -> handleFineClicked()
            is MonopolyAction.StatementsLoaded -> currentState.copy(
                fines = action.fines.toImmutableList(),
                prizes = action.prizes.toImmutableList(),
            )

            is MonopolyAction.QuestionsLoaded -> currentState.copy(
                questions = action.questions.toImmutableList(),
            ).also {
                launchEffect { sideEffectFlow.emit(MonopolyEffect.ShowToast("התחילו לשחק")) }
            }

            is MonopolyAction.LoadError -> currentState
        }

    private fun handleDiceClicked(): MonopolyState {
        if (currentState.isRolling) return currentState
        launchEffect {
            sideEffectFlow.emit(MonopolyEffect.PlayRollSound)
            sideEffectFlow.emit(MonopolyEffect.VibrateShort)
            delay(400.milliseconds)
            dispatch(MonopolyAction.DiceRollFinished)
        }
        return currentState.copy(
            isRolling = true,
            diceImage = SharedRes.images.dice3droll,
            isRevealVisible = false,
        )
    }

    private fun handleDiceRollFinished(): MonopolyState {
        val num = Random.nextInt(6)
        val steps = num + 1
        val instruction = if (steps == 1) "התקדם צעד 1" else "התקדם $steps צעדים"
        return currentState.copy(
            isRolling = false,
            diceImage = diceImages[num],
            pasukText = instruction,
        )
    }

    private fun handleQuestionClicked(): MonopolyState {
        if (currentState.questions.isEmpty()) return currentState
        val item = currentState.questions[Random.nextInt(currentState.questions.size)]
        val answer = item.answer.orEmpty()
        val revealText = "תשובה: ${answer.ifEmpty { "/" }}"
        launchEffect {
            sideEffectFlow.emit(MonopolyEffect.PlayQuestionSound)
            sideEffectFlow.emit(MonopolyEffect.VibrateShort)
        }
        return currentState.copy(
            pasukText = item.question.orEmpty(),
            isRevealVisible = true,
            revealText = revealText,
        )
    }

    private fun handlePrizeClicked(): MonopolyState {
        if (currentState.prizes.isEmpty()) return currentState
        val message = currentState.prizes[Random.nextInt(currentState.prizes.size)]
        launchEffect {
            sideEffectFlow.emit(MonopolyEffect.PlayPrizeSound)
            sideEffectFlow.emit(MonopolyEffect.VibrateMedium)
        }
        return currentState.copy(
            pasukText = message,
            isRevealVisible = false,
        )
    }

    private fun handleFineClicked(): MonopolyState {
        if (currentState.fines.isEmpty()) return currentState
        val message = currentState.fines[Random.nextInt(currentState.fines.size)]
        launchEffect {
            sideEffectFlow.emit(MonopolyEffect.PlayFineSound)
            sideEffectFlow.emit(MonopolyEffect.VibrateLong)
        }
        return currentState.copy(
            pasukText = message,
            isRevealVisible = false,
        )
    }
}
